import { useCallback, useEffect, useRef, useState } from "react";

// WONE is a UK product, so fall back to en-GB when the browser has no locale.
const FALLBACK_LANG = "en-GB";

// The Web Speech API types aren't in TS's lib.dom.d.ts — declare the shape we use.
interface SpeechRecognitionAlternativeLike {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}
interface SpeechRecognitionEventLike extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
}
interface SpeechRecognitionErrorEventLike extends Event {
  readonly error: string;
  readonly message: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

const getRecognitionConstructor = (): SpeechRecognitionConstructor | undefined => {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
};

export type VoiceInputState = "idle" | "listening" | "error";

interface UseVoiceInputOptions {
  onInterim?: (text: string) => void;
  /** The full accumulated transcript, emitted once on stop. */
  onFinal: (text: string) => void;
  onStart?: (meta: { locale: string }) => void;
  onError?: (errorCode: string) => void;
}

interface UseVoiceInputReturn {
  isSupported: boolean;
  state: VoiceInputState;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

export function useVoiceInput({
  onInterim,
  onFinal,
  onStart,
  onError,
}: UseVoiceInputOptions): UseVoiceInputReturn {
  const [state, setState] = useState<VoiceInputState>("idle");

  const onInterimRef = useRef(onInterim);
  const onFinalRef = useRef(onFinal);
  const onStartRef = useRef(onStart);
  const onErrorRef = useRef(onError);
  onInterimRef.current = onInterim;
  onFinalRef.current = onFinal;
  onStartRef.current = onStart;
  onErrorRef.current = onError;

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // Continuous mode fires multiple final segments; join them, emit once on `onend`.
  const accumulatedRef = useRef("");
  // Set by `abort()` so `onend` skips the final emission.
  const abortedRef = useRef(false);

  const isSupported = getRecognitionConstructor() !== undefined;

  const start = useCallback(() => {
    if (recognitionRef.current) return;
    const Recognition = getRecognitionConstructor();
    if (!Recognition) return;

    const recognition = new Recognition();
    const locale = navigator.language || FALLBACK_LANG;
    recognition.lang = locale;
    recognition.continuous = true;
    recognition.interimResults = true;

    accumulatedRef.current = "";
    abortedRef.current = false;

    recognition.onstart = () => {
      setState("listening");
      onStartRef.current?.({ locale });
    };

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? "";
        if (result.isFinal) {
          const trimmed = transcript.trim();
          if (trimmed) {
            accumulatedRef.current = accumulatedRef.current
              ? `${accumulatedRef.current} ${trimmed}`
              : trimmed;
          }
        } else {
          interim = interim ? `${interim} ${transcript}` : transcript;
        }
      }
      const live = [accumulatedRef.current, interim]
        .filter(Boolean)
        .join(" ")
        .trim();
      onInterimRef.current?.(live);
    };

    recognition.onerror = (event) => {
      const errorCode = event.error;
      // "no-speech" / "aborted" are quiet cancels resolved by `onend`, not failures.
      if (errorCode === "no-speech" || errorCode === "aborted") return;
      abortedRef.current = true;
      onErrorRef.current?.(errorCode);
      setState("error");
    };

    recognition.onend = () => {
      const full = accumulatedRef.current;
      accumulatedRef.current = "";
      recognitionRef.current = null;
      const aborted = abortedRef.current;
      abortedRef.current = false;
      setState((prev) => (prev === "error" ? "error" : "idle"));
      if (!aborted && full) {
        onFinalRef.current(full);
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const abort = useCallback(() => {
    accumulatedRef.current = "";
    abortedRef.current = true;
    recognitionRef.current?.abort();
  }, []);

  useEffect(
    () => () => {
      const recognition = recognitionRef.current;
      if (recognition) {
        accumulatedRef.current = "";
        abortedRef.current = true;
        recognition.abort();
        recognitionRef.current = null;
      }
    },
    []
  );

  return { isSupported, state, start, stop, abort };
}
